import { useEffect, useMemo, useState } from "react";
import { Bell, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canManageNotificationTemplates } from "@/lib/permissions";
import { Navigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

// Client-side fallback so admins see the standard system phases even before
// the backend settings endpoint returns. Kept in sync with the seed list.
const FALLBACK_PHASES = [
  "Idea - Initial",
  "Idea - Update",
  "Open - Entry",
  "Open - Added",
  "Open - Management",
  "Open - Trimmed",
  "Closed",
  "Closed - Update",
  "Dead",
];

function PhaseCard({ phase, initial, defaults, variables, onSaved, onDeleted }) {
  const [emailSubject, setEmailSubject] = useState(initial?.email_subject || "");
  const [emailBody, setEmailBody] = useState(initial?.email_body || "");
  const [smsBody, setSmsBody] = useState(initial?.sms_body || "");
  const [busy, setBusy] = useState(false);
  const hasOverride = !!(initial?.email_subject || initial?.email_body || initial?.sms_body);

  const insertVar = (field, setter, current) => (v) => {
    const el = document.getElementById(`${phase}-${field}`);
    if (!el) { setter((current || "") + `{${v}}`); return; }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + `{${v}}` + current.slice(end);
    setter(next);
    // Restore focus and cursor position after the placeholder.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + v.length + 2;
      try { el.setSelectionRange(pos, pos); } catch { /* ignore */ }
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/notification-templates/${encodeURIComponent(phase)}`, {
        email_subject: emailSubject, email_body: emailBody, sms_body: smsBody,
      });
      toast.success(`Saved "${phase}"`);
      onSaved({ phase, email_subject: emailSubject || null, email_body: emailBody || null, sms_body: smsBody || null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/notification-templates/${encodeURIComponent(phase)}`);
      setEmailSubject(""); setEmailBody(""); setSmsBody("");
      toast.success(`Reset "${phase}" to defaults`);
      onDeleted(phase);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const VarChip = ({ v, field, setter, current }) => (
    <button
      type="button"
      onClick={() => insertVar(field, setter, current)(v)}
      className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-zinc-800 hover:border-blue-500/50 hover:text-blue-400 text-zinc-500 rounded-sm"
      data-testid={`tpl-${phase}-var-${field}-${v}`}
    >
      {`{${v}}`}
    </button>
  );

  return (
    <div className="border border-zinc-900 rounded-sm bg-[#080808]" data-testid={`tpl-card-${phase}`}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-zinc-900">
        <div className="flex items-center gap-2">
          <span className="font-heading text-sm uppercase tracking-wider text-zinc-200">{phase}</span>
          {hasOverride ? (
            <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 px-1.5 py-0.5 border border-emerald-500/30 rounded-sm">Custom</span>
          ) : (
            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 px-1.5 py-0.5 border border-zinc-800 rounded-sm">Default</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasOverride && (
            <Button variant="ghost" size="sm" onClick={reset} disabled={busy} data-testid={`tpl-reset-${phase}`} className="h-7 text-[11px] uppercase tracking-wider text-zinc-500 hover:text-zinc-200">
              <RotateCcw className="w-3 h-3 mr-1" /> Reset
            </Button>
          )}
          <Button size="sm" onClick={save} disabled={busy} data-testid={`tpl-save-${phase}`} className="h-7 text-[11px] uppercase tracking-wider bg-blue-600 hover:bg-blue-500 rounded-sm">
            <Save className="w-3 h-3 mr-1" /> Save
          </Button>
        </div>
      </header>

      <div className="p-4 space-y-4">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Email Subject</Label>
            <span className="text-[10px] text-zinc-600 font-mono">default: {defaults?.email_subject}</span>
          </div>
          <Input
            id={`${phase}-email_subject`}
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder={defaults?.email_subject}
            data-testid={`tpl-${phase}-email-subject`}
            className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm"
          />
          <div className="flex flex-wrap gap-1 mt-1.5">
            {variables.map((v) => <VarChip key={v} v={v} field="email_subject" setter={setEmailSubject} current={emailSubject} />)}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Email Body</Label>
            <span className="text-[10px] text-zinc-600 font-mono">line breaks are preserved</span>
          </div>
          <Textarea
            id={`${phase}-email_body`}
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            placeholder={defaults?.email_body}
            rows={5}
            data-testid={`tpl-${phase}-email-body`}
            className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm"
          />
          <div className="flex flex-wrap gap-1 mt-1.5">
            {variables.map((v) => <VarChip key={v} v={v} field="email_body" setter={setEmailBody} current={emailBody} />)}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">SMS Body</Label>
            <span className="text-[10px] text-zinc-600 font-mono">{smsBody.length}/160</span>
          </div>
          <Textarea
            id={`${phase}-sms_body`}
            value={smsBody}
            onChange={(e) => setSmsBody(e.target.value)}
            placeholder={defaults?.sms_body}
            rows={3}
            data-testid={`tpl-${phase}-sms-body`}
            className="bg-zinc-950 border-zinc-800 rounded-sm font-mono text-sm"
          />
          <div className="flex flex-wrap gap-1 mt-1.5">
            {variables.map((v) => <VarChip key={v} v={v} field="sms_body" setter={setSmsBody} current={smsBody} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function NotificationTemplates() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [variables, setVariables] = useState([]);
  const [phases, setPhases] = useState(FALLBACK_PHASES);

  useEffect(() => {
    if (!canManageNotificationTemplates(user)) return;
    (async () => {
      try {
        const [tplRes, phaseRes] = await Promise.all([
          api.get("/admin/notification-templates"),
          api.get("/admin/protected-phases").catch(() => ({ data: { phases: [] } })),
        ]);
        setTemplates(tplRes.data.templates || []);
        setDefaults(tplRes.data.defaults || {});
        setVariables(tplRes.data.variables || []);
        const merged = Array.from(new Set([...(phaseRes.data.phases || []), ...FALLBACK_PHASES]));
        setPhases(merged);
      } catch (e) {
        toast.error("Failed to load templates");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const byPhase = useMemo(() => {
    const map = {};
    for (const t of templates) map[t.phase] = t;
    return map;
  }, [templates]);

  const onSaved = (row) =>
    setTemplates((prev) => {
      const idx = prev.findIndex((t) => t.phase === row.phase);
      if (idx === -1) return [...prev, row];
      const next = [...prev];
      next[idx] = { ...next[idx], ...row };
      return next;
    });
  const onDeleted = (phase) => setTemplates((prev) => prev.filter((t) => t.phase !== phase));

  if (!canManageNotificationTemplates(user)) {
    return <Navigate to="/journal" replace />;
  }

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="notification-templates-page">
      <header className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Bell className="w-5 h-5 text-blue-500" />
          <h1 className="font-heading text-2xl uppercase tracking-wider text-zinc-100">Notification Templates</h1>
        </div>
        <p className="text-sm text-zinc-500 max-w-2xl">
          Customize the Email and SMS content sent when each trade phase is added. Click a{" "}
          <span className="font-mono text-blue-400">{`{variable}`}</span> chip to insert it at the cursor. Leaving a
          field blank falls back to the system default shown as placeholder.
        </p>
      </header>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : (
        <div className="space-y-4">
          {phases.map((p) => (
            <PhaseCard
              key={p}
              phase={p}
              initial={byPhase[p]}
              defaults={defaults}
              variables={variables}
              onSaved={onSaved}
              onDeleted={onDeleted}
            />
          ))}
        </div>
      )}
    </div>
  );
}
